import axios from "../axios";

export interface IProviderModelGroup {
  month: number;
  micro: number;
  total: number;
  monthPercent: number;
  microPercent: number;
}

export interface IProviderBreakdown {
  credentialed: IProviderModelGroup;
  all: IProviderModelGroup;
  credentialedAdoptionPercent: number;
}

export interface IReportSummary {
  currentWeekLeads: number;
  previousWeekLeads: number;
  leadChangePercent: number;
  totalLeads: number;
  totalLeads30Days?: number;
  activeProviders: number;
  subscriptionPercent: number;
  microPercent: number;
  matchedLeads?: number;
  needsAttentionLeads?: number;
  matchRatePercent?: number;
  credentialedProviders?: number;
  credentialedMonthProviders?: number;
  credentialedMicroProviders?: number;
  allMonthProviders?: number;
  allMicroProviders?: number;
  credentialedAdoptionPercent?: number;
}

export interface IReportWeeklyData {
  weekLabel: string;
  startDate: string;
  leads: number;
  cumulativeLeads: number;
  activityLeadsCreated: number;
  activityLeadsUpdated: number;
  activityLogins: number;
  activityLeadsOpened: number;
  activityOffersSaved: number;
  activityProfileUpdates: number;
  totalActivity: number;
  subscriptionProviders: number;
  microProviders: number;
}

export interface IReportAttribution {
  DIRECT: number;
  AI: number;
  PARTNERS: number;
  SEARCH: number;
  OTHER: number;
}

export interface IMunicipalityGap {
  municipality: string;
  serviceId?: number;
  serviceName?: string;
  count: number;
  zeroViewsCount: number;
  offerCount?: number;
  latitude?: number;
  longitude?: number;
}

export interface IDemandGaps {
  matchedLeads: number;
  needsHelpLeads: number;
  matchRatePercent: number;
  zeroOfferRatePercent: number;
  municipalities: IMunicipalityGap[];
}

export interface IMatchingProvider {
  id: number;
  name: string;
  city?: string;
  nearestCity?: string;
  distanceKm: number;
  operatingRadius: number;
  paymentMethod?: string;
  emails: string[];
}

export interface IMatchingProvidersResponse {
  municipality: string;
  providers: IMatchingProvider[];
  totalFound: number;
}

export interface IFeedbackOverview {
  averageRating: number;
  periodFeedbackCount: number;
  positivePercent: number;
  totalFeedbackCount: number;
  ratedCount?: number;
}

export interface IInvoicingOverview {
  paidAmount: number;
  openAmount: number;
  overdueAmount: number;
  paidCount: number;
  openCount: number;
  overdueCount: number;
}

export interface IRevenueOverview {
  subscriptionPrice: number;
  microPricePerLead: number;
  estimatedMonthlyRecurring: number;
  periodSubscriptionRevenue: number;
  microLeadsSold: number;
  periodMicroRevenue: number;
  totalPeriodRevenue: number;
  invoicing: IInvoicingOverview;
}

export interface IReportResponse {
  summary: IReportSummary;
  providerBreakdown?: IProviderBreakdown;
  timeSeries: IReportWeeklyData[];
  attribution: IReportAttribution;
  demandGaps?: IDemandGaps;
  feedback?: IFeedbackOverview;
  revenue?: IRevenueOverview;
}

export const getReportData = async (serviceIds: string[], operatorId?: string, weeks?: number): Promise<IReportResponse | null> => {
  try {
    const resp = await axios.post("/json/reports/get", {
      serviceIds,
      operatorId,
      weeks
    });
    return resp.data;
  } catch (error) {
    console.error("Failed to fetch report data:", error);
    return null;
  }
};

export const getMatchingProviders = async (
  municipality: string,
  serviceId?: number,
  latitude?: number,
  longitude?: number
): Promise<IMatchingProvidersResponse | null> => {
  try {
    const resp = await axios.post("/json/reports/matching-providers", {
      municipality,
      serviceId,
      latitude,
      longitude
    });
    return resp.data;
  } catch (error) {
    console.error("Failed to fetch matching providers:", error);
    return null;
  }
};
